import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Layout } from "../../components/Layout";
import { usePushMessage } from "../../providers";
import { useAuthTools, usePermissions } from "../../providers/authProvider";
import { Button } from "../../components/Button";
import { USER_AUTH_URL, USER_PROFILE_URL } from "../../config";
import { Input } from "../../components/forms";
import { Loader } from "../../components/Loader";
import { UserData } from "../../../../types/auth";

type FormType = {
  name: string;
  email: string;
};

type InputType<Form> = {
  id: string;
  name: keyof Form;
  type?: "password" | "email" | "text"; // defaults to text
  label: string;
  placeholder: string;
  autoComplete: string;
  required?: true;
  disabled?: boolean;
};
type GetTextReturnType = {
  title: string;
  inputs: InputType<FormType>[];
  CTA: { text: string };
  secondaryCTA?: { text: string; href: string; reset?: boolean };
  tertiaryCTA?: { text: string; href: string; reset?: boolean };
};
const getText = (): GetTextReturnType => {
  const name = {
    id: "name",
    label: "Name",
    name: "name",
    placeholder: "Alex Decanter",
    autoComplete: "name",
    required: true,
  } as const;
  const email = {
    id: "email",
    label: "Email",
    name: "email",
    placeholder: "SparkySilver@gmail.com",
    autoComplete: "email",
    type: "email",
    required: true,
    disabled: true,
    title:
      "Sorry, you can't update your email, please create a new account instead.",
  } as const;
  const CTA = { text: "Save" };

  return {
    title: "Profile",
    inputs: [email, name],
    CTA,
  };
};
export const User = ({ userData }: { userData: UserData }) => {
  const { updateUser } = useAuthTools();
  const navigate = useNavigate();
  const location = useLocation();
  const [validationError, setValidationError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormType>(userData);
  const reset = useCallback(() => {
    setForm(() => userData);
    setClicked(0);
  }, [setForm]);
  const pushMessage = usePushMessage();
  const validate = (form: FormType, inputs: InputType<FormType>[]) => {
    const firstMissingRequiredInput = inputs
      .filter((input) => !!input.required)
      .find((input) => !form[input.name]);
    if (firstMissingRequiredInput) {
      return "Please enter " + firstMissingRequiredInput.label;
    }
    if (form.name?.length < 2) {
      return "Please enter a longer name";
    }
    return true;
  };

  const { title, inputs, CTA, secondaryCTA, tertiaryCTA } = getText();

  const update = useCallback(
    ({ name }: FormType): Promise<void | undefined> | undefined => {
      if (!updateUser) {
        console.log("LOGIN/SIGNUP DOESN'T EXIST YET!");
        return;
      }
      //TODO Validation
      setLoading(true);
      const validationResult = validate(form, inputs);
      if (typeof validationResult === "string") {
        setLoading(false);
        setValidationError(validationResult);
        return;
      } else if (!validationResult) {
        setLoading(false);
        setValidationError("Please check the fields are valid");
        return;
      }
      setValidationError("");
      return updateUser({ name })
        .then(
          (data: {
            success: boolean;
            // user?: any;
            error?: string;
            // expiration?: number;
          }) => {
            if (data.success) {
              const msgs = [
                "Thank you so much for updating!",
                "On it.",
                "Thanks!",
                "Will do.",
                "Noted.",
                "Message received.",
                "Pretty sure we got that.",
                "Ok, thanks.",
              ];
              pushMessage({
                message: msgs[Math.floor(Math.random() * msgs.length)],
                type: "info",
                ttl: 10000,
              });

              setLoading(false);

              return;
            } else {
              setValidationError(data.error as string);
              setLoading(false);
            }
            reset();
            // setLoading(false);
          },
        )
        .catch((e) => {
          setLoading(false);
          pushMessage({
            message: e.message,
            type: "error",
            ttl: 10000,
          });
        });
    },
    [setLoading, navigate, inputs, form],
  );

  const [clicked, setClicked] = useState(0);
  useEffect(() => {
    setClicked(0);
  }, [setClicked, location.pathname]);
  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = evt.target;
    setForm((form) => ({
      ...form,
      [name]: value,
    }));
  };
  const buttonClass = " duration-700  " + (clicked ? " opacity-0 " : "");

  return (
    <Layout headerStyle="sm">
      <div>
        <form
          className="flex flex-col items-center px-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setClicked(1);
            try {
              update(form)?.then(() => {
                setClicked(0);
              });
            } catch (e: any) {
              pushMessage({ type: "error", message: e.message });
            }
          }}
        >
          <h1>{title}</h1>

          {inputs.map((inputDetails) => (
            <Input
              inputType="input"
              key={inputDetails.id}
              className={"max-w-full w-[500px] my-2 "}
              {...inputDetails}
              disabled={inputDetails.disabled || loading}
              value={form[inputDetails.name]}
              onChange={handleChange}
            />
          ))}
          <div className="text-textWarning">{validationError}</div>
          <div className=" my-2 w-full  max-w-[500px]">
            <Button
              type="submit"
              loading={loading}
              disabled={
                loading ||
                inputs.every(
                  (inputDetails) =>
                    form[inputDetails.name] === userData[inputDetails.name],
                )
              }
              className={clicked !== 1 ? buttonClass : undefined}
              btnColor="primary"
              btnType="large"
            >
              {CTA.text}
            </Button>
          </div>
          {secondaryCTA ? (
            <div className="my-2 w-full max-w-[500px]">
              <Button
                type="button"
                loading={loading}
                disabled={loading}
                btnColor="secondary"
                btnType="large"
                className={clicked !== 2 ? buttonClass : undefined}
                onClick={() => {
                  setClicked(2);
                  if (secondaryCTA.reset) {
                    reset();
                  }
                  secondaryCTA.href === location.pathname
                    ? window.location.reload()
                    : navigate(secondaryCTA.href);
                  setForm((form) => ({
                    ...form,
                    password: "",
                    "confirm-password": "",
                  }));
                }}
              >
                {secondaryCTA.text}
              </Button>
            </div>
          ) : null}

          {tertiaryCTA ? (
            <div className="my-2 w-full max-w-[500px]">
              <Button
                type="button"
                loading={loading}
                disabled={loading}
                btnColor="text"
                className={clicked !== 3 ? buttonClass : undefined}
                btnType="text"
                onClick={() => {
                  setClicked(3);
                  if (tertiaryCTA.reset) {
                    reset();
                  }
                  tertiaryCTA.href === location.pathname
                    ? window.location.reload()
                    : navigate(tertiaryCTA.href);
                  setForm((form) => ({
                    ...form,
                    password: "",
                    "confirm-password": "",
                  }));
                }}
              >
                {tertiaryCTA.text}
              </Button>
            </div>
          ) : null}
        </form>
      </div>
    </Layout>
  );
};

// Wrapper component that uses React Router
export function UserPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const validSlugs = ["me"]; // base /user/profile path
  // only own profile is allowed right now
  const permissions = usePermissions();

  useEffect(() => {
    if (!validSlugs.includes(slug as string)) {
      navigate(USER_PROFILE_URL());
      return;
    }
    if (permissions?.loading) {
      return;
    }
    if (!permissions?.data?.loggedIn) {
      navigate(
        USER_AUTH_URL("login", { flow: "resume", to: location.pathname }),
      );
      return;
    }
  }, [slug, permissions?.loading, permissions?.data?.loggedIn]);
  if (!validSlugs.includes(slug as string)) {
    return null;
  }
  if (permissions?.loading) {
    return (
      <Loader
        type="text"
        dot=" ..._ _ _... "
        dotMaxLength={10000}
        dotInterval={100}
      >
        <div>WE ARE FURIOUSLY ATTEMPTING TO LOAD THIS PAGE</div>
      </Loader>
    );
  }
  if (!permissions?.data?.loggedIn) {
    return null;
  }

  if (permissions?.error) {
    return (
      <div>
        <p>Sorry, There was an error</p>
        <p>{permissions.error}</p>
      </div>
    );
  }
  if (!permissions?.data?.user) {
    return (
      <div>
        <p>Sorry, We can't find a user for you?</p>
        {Object.entries(permissions.data).map(([k, v]) => (
          <p>
            {k}:{" "}
            {typeof v !== "object"
              ? v
              : Object.entries(v).map(([k2, v2]) => (
                  <p>
                    {k2}: {v2}
                  </p>
                ))}
          </p>
        ))}
      </div>
    );
  }

  return <User userData={permissions.data.user} />;
}
export default UserPage;
